<script setup lang="ts">
/**
 * Dialog form for "Request a new AI" — staff submit a use case +
 * business case to the DOIT AI Board queue. Lightweight modal
 * with five free-text fields; only `use_case` is required so the
 * form has low friction. The Board fills in gaps during review
 * (see AdminAiRequests for the queue side).
 *
 * Opened from AssistantHub via v-model. Posts to /api/ai-requests
 * via `submitAiRequest`. On success: shows the success state with
 * the request id and a "submit another" button.
 */
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import {
  submitAiRequest,
  AiRequestError,
  type AiRequestOut,
} from "../api/conversations";

const props = defineProps<{ modelValue: boolean; devUser?: string }>();
const emit = defineEmits<{ (e: "update:modelValue", v: boolean): void }>();

/** The first input the keyboard should land in when the dialog
 * opens. Bound via template ref; focused inside the v-model watcher
 * so a screen-reader user lands in the use-case field, not on the
 * backdrop. */
const useCaseRef = ref<HTMLTextAreaElement | null>(null);

/** Document-level Escape handler. Attached only while the dialog is
 * open; removed on close + before unmount so we don't leak listeners
 * across re-opens. */
function onKeydown(e: KeyboardEvent): void {
  if (e.key === "Escape") {
    e.preventDefault();
    close();
  }
}

watch(() => props.modelValue, (open) => {
  if (open) {
    document.addEventListener("keydown", onKeydown);
    nextTick(() => useCaseRef.value?.focus());
  } else {
    document.removeEventListener("keydown", onKeydown);
  }
});

onBeforeUnmount(() => document.removeEventListener("keydown", onKeydown));

const useCase = ref("");
const proposedName = ref("");
const proposedSlug = ref("");
const dataSources = ref("");
const targetAudience = ref("");
const businessCase = ref("");

const busy = ref(false);
const errorMsg = ref<string | null>(null);
const submitted = ref<AiRequestOut | null>(null);

function close(): void {
  emit("update:modelValue", false);
  // Don't clear fields immediately so a re-open after dismiss
  // keeps the user's draft. Submission resets explicitly.
}

function reset(): void {
  useCase.value = "";
  proposedName.value = "";
  proposedSlug.value = "";
  dataSources.value = "";
  targetAudience.value = "";
  businessCase.value = "";
  submitted.value = null;
  errorMsg.value = null;
}

async function submit(): Promise<void> {
  if (!useCase.value.trim() || busy.value) return;
  busy.value = true;
  errorMsg.value = null;
  try {
    submitted.value = await submitAiRequest(
      {
        use_case: useCase.value.trim(),
        proposed_name: proposedName.value.trim() || undefined,
        proposed_slug: proposedSlug.value.trim() || undefined,
        data_sources: dataSources.value.trim() || undefined,
        target_audience: targetAudience.value.trim() || undefined,
        business_case: businessCase.value.trim() || undefined,
      },
      props.devUser,
    );
  } catch (e) {
    errorMsg.value = e instanceof AiRequestError
      ? `${e.message} (${e.status})`
      : String(e);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="dialog__backdrop"
      @click.self="close"
    >
      <div
        class="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reqai-title"
      >
        <header class="dialog__header">
          <h2 id="reqai-title">Request a new AI</h2>
          <button type="button" class="dialog__close" @click="close" aria-label="Close">×</button>
        </header>

        <!-- Success state -->
        <section v-if="submitted" class="dialog__body">
          <p class="success">
            Thanks — your request is in the queue.
            <strong>Reference #{{ submitted.id }}</strong>
          </p>
          <p class="success-hint">
            The internal AI Board prioritises new assistants by potential
            savings, effort, and business case. They'll get back to you
            with a decision; you can track the status from your profile
            once review starts.
          </p>
          <div class="dialog__actions">
            <button class="btn btn--secondary" @click="reset">Submit another</button>
            <button class="btn btn--primary" @click="close">Done</button>
          </div>
        </section>

        <!-- Form state -->
        <section v-else class="dialog__body">
          <p class="intro">
            Help the AI Board scope your idea. Only the use case is
            required — the rest is helpful but optional.
          </p>

          <form class="dialog__form" @submit.prevent="submit">
            <label class="field field--required">
              <span class="field__label">What should the assistant do?</span>
              <textarea
                ref="useCaseRef"
                v-model="useCase"
                class="field__input"
                rows="3"
                placeholder="e.g. Answer HR-policy questions for managers — leave, travel, benefits, expense rules."
                required
              />
            </label>

            <div class="field-row">
              <label class="field">
                <span class="field__label">Proposed name</span>
                <input
                  v-model="proposedName"
                  class="field__input"
                  type="text"
                  placeholder="HR Policy Helper"
                  maxlength="200"
                />
              </label>
              <label class="field">
                <span class="field__label">Slug (optional)</span>
                <input
                  v-model="proposedSlug"
                  class="field__input"
                  type="text"
                  placeholder="hr-policy"
                  maxlength="64"
                  pattern="[a-z0-9\-]+"
                />
              </label>
            </div>

            <label class="field">
              <span class="field__label">Data sources</span>
              <textarea
                v-model="dataSources"
                class="field__input"
                rows="2"
                placeholder="e.g. SharePoint HR site, the 2026 benefits PDF, weekly travel update emails."
              />
            </label>

            <label class="field">
              <span class="field__label">Who will use it?</span>
              <input
                v-model="targetAudience"
                class="field__input"
                type="text"
                placeholder="e.g. All staff, or just Sales"
              />
            </label>

            <label class="field">
              <span class="field__label">Business case / expected value</span>
              <textarea
                v-model="businessCase"
                class="field__input"
                rows="2"
                placeholder="e.g. Saves ~30 min/week per manager (≈ 50 managers). Reduces ad-hoc HR ticket load."
              />
            </label>

            <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

            <div class="dialog__actions">
              <button type="button" class="btn btn--secondary" @click="close" :disabled="busy">
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn--primary"
                :disabled="busy || !useCase.trim()"
              >
                {{ busy ? "Submitting…" : "Submit to AI Board" }}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.dialog__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 29, 46, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: var(--space-4);
}
.dialog {
  background: var(--color-surface);
  border-radius: var(--radius-lg, 12px);
  max-width: 640px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 48px rgba(15, 29, 46, 0.3);
  display: flex;
  flex-direction: column;
}
.dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border);
}
.dialog__header h2 {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: var(--color-ink);
}
.dialog__close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: var(--color-muted);
  cursor: pointer;
  padding: 0 0.4rem;
}
.dialog__close:hover { color: var(--color-ink); }
.dialog__body { padding: var(--space-4) var(--space-5); }
.intro {
  color: var(--color-muted);
  font-size: 0.95rem;
  margin: 0 0 var(--space-4);
}
.success {
  background: var(--color-accent-dim);
  border: 1px solid var(--color-accent);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  margin: 0 0 var(--space-3);
  color: var(--color-ink);
}
.success strong { color: var(--color-accent); }
.success-hint {
  color: var(--color-muted);
  font-size: 0.9rem;
  line-height: 1.55;
  margin: 0 0 var(--space-4);
}
.dialog__form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-ink);
}
.field--required .field__label::after {
  content: " *";
  color: var(--color-error);
}
.field__input {
  font-family: inherit;
  font-size: 0.95rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
  width: 100%;
  resize: vertical;
}
.field__input:focus {
  outline: 2px solid var(--color-accent);
  outline-offset: -1px;
  border-color: var(--color-accent);
}
.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}
@media (max-width: 540px) {
  .field-row { grid-template-columns: 1fr; }
}
.error {
  color: var(--color-error);
  background: rgb(183 28 28 / 0.06);
  border: 1px solid var(--color-error);
  padding: 0.5rem 0.7rem;
  border-radius: var(--radius-sm);
  margin: 0;
  font-size: 0.9rem;
}
.dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  margin-top: var(--space-3);
}
.btn {
  font: inherit;
  padding: 0.55rem 1.2rem;
  border-radius: var(--radius-md);
  border: 1px solid transparent;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.95rem;
}
.btn--primary {
  background: var(--color-accent);
  color: white;
}
.btn--primary:hover:not(:disabled) {
  background: var(--color-accent-hover);
}
.btn--primary:disabled {
  background: var(--color-border);
  color: var(--color-muted);
  cursor: not-allowed;
}
.btn--secondary {
  background: transparent;
  border-color: var(--color-border);
  color: var(--color-ink);
}
.btn--secondary:hover:not(:disabled) {
  background: var(--color-surface-alt);
}
</style>
